"use client";
/*
 * Documentation:
 * Button — https://app.subframe.com/4f62853ee923/library?component=Button_3b777358-b86b-40af-9327-891efc6826fe
 * DC_MetaIntro_ActionStack — https://app.subframe.com/4f62853ee923/library?component=DC_MetaIntro_ActionStack_c2a1f933-7ab4-4ec2-94fe-f4cfdc04d0e0
 */

import React from "react";
import { FeatherCheck } from "@subframe/core";
import { FeatherClipboard } from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";
import { Button } from "./Button";

interface PdfDownloadButtonProps extends React.ComponentProps<typeof Button> {
  children?: React.ReactNode;
  className?: string;
}

const PdfDownloadButton = React.forwardRef<
  React.ElementRef<typeof Button>,
  PdfDownloadButtonProps
>(function PdfDownloadButton(
  { children, className, ...otherProps }: PdfDownloadButtonProps,
  ref
) {
  return (
    <Button
      className={className}
      disabled={false}
      size="large"
      iconRight={null}
      loading={false}
      ref={ref}
      {...otherProps}
    >
      {children}
    </Button>
  );
});

interface ListenButtonProps extends React.ComponentProps<typeof Button> {
  children?: React.ReactNode;
  className?: string;
}

const ListenButton = React.forwardRef<
  React.ElementRef<typeof Button>,
  ListenButtonProps
>(function ListenButton(
  { children, className, ...otherProps }: ListenButtonProps,
  ref
) {
  return (
    <Button
      className={className}
      disabled={false}
      variant="brand-secondary"
      size="large"
      loading={false}
      ref={ref}
      {...otherProps}
    >
      {children}
    </Button>
  );
});

interface DoiButtonProps extends React.ComponentProps<typeof Button> {
  popoverSlot?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const DoiButton = React.forwardRef<
  React.ElementRef<typeof Button>,
  DoiButtonProps
>(function DoiButton(
  { popoverSlot, children, className, ...otherProps }: DoiButtonProps,
  ref
) {
  return (
    <SubframeCore.Popover.Root>
      <SubframeCore.Popover.Trigger asChild={true}>
        <Button
          className={className}
          disabled={false}
          variant="neutral-secondary"
          size="large"
          loading={false}
          ref={ref}
          {...otherProps}
        >
          {children}
        </Button>
      </SubframeCore.Popover.Trigger>
      <SubframeCore.Popover.Portal>
        <SubframeCore.Popover.Content
          side="top"
          align="center"
          sideOffset={12}
          asChild={true}
        >
          {popoverSlot ? (
            <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background shadow-lg">
              {popoverSlot}
            </div>
          ) : null}
        </SubframeCore.Popover.Content>
      </SubframeCore.Popover.Portal>
    </SubframeCore.Popover.Root>
  );
});

interface DoiPopoverProps extends React.HTMLAttributes<HTMLDivElement> {
  linkSlot?: React.ReactNode;
  interactionSlot?: React.ReactNode;
  className?: string;
}

const DoiPopover = React.forwardRef<HTMLDivElement, DoiPopoverProps>(
  function DoiPopover(
    { linkSlot, interactionSlot, className, ...otherProps }: DoiPopoverProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex flex-col items-start gap-2",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <div className="flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-3 shadow-lg comp-popover">
          {linkSlot ? (
            <div className="flex flex-col items-start gap-4">{linkSlot}</div>
          ) : null}
          {interactionSlot ? (
            <div className="flex w-full flex-col items-start gap-4">
              {interactionSlot}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
);

interface CiteButtonProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  popoverSlot?: React.ReactNode;
  className?: string;
}

const CiteButton = React.forwardRef<HTMLDivElement, CiteButtonProps>(
  function CiteButton(
    { children, popoverSlot, className, ...otherProps }: CiteButtonProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex flex-col items-start gap-2",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <SubframeCore.Popover.Root>
          <SubframeCore.Popover.Trigger asChild={true}>
            <Button
              disabled={false}
              variant="neutral-secondary"
              size="large"
              loading={false}
            >
              {children}
            </Button>
          </SubframeCore.Popover.Trigger>
          <SubframeCore.Popover.Portal>
            <SubframeCore.Popover.Content
              side="top"
              align="center"
              sideOffset={12}
              asChild={true}
            >
              {popoverSlot ? (
                <div className="flex items-start justify-between">
                  {popoverSlot}
                </div>
              ) : null}
            </SubframeCore.Popover.Content>
          </SubframeCore.Popover.Portal>
        </SubframeCore.Popover.Root>
      </div>
    );
  }
);

interface CitePopoverProps extends React.HTMLAttributes<HTMLDivElement> {
  version?: React.ReactNode;
  citation?: React.ReactNode;
  doi?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const CitePopover = React.forwardRef<HTMLDivElement, CitePopoverProps>(
  function CitePopover(
    {
      version,
      citation,
      doi,
      children,
      className,
      ...otherProps
    }: CitePopoverProps,
    ref
  ) {
    return children ? (
      <div
        className={SubframeUtils.twClassNames(
          "flex max-w-[448px] flex-col items-start gap-4 comp-popover",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {children}
      </div>
    ) : null;
  }
);

interface CopyInteractionProps extends React.HTMLAttributes<HTMLDivElement> {
  text?: React.ReactNode;
  isPressed?: boolean;
  copiedText?: React.ReactNode;
  className?: string;
}

const CopyInteraction = React.forwardRef<HTMLDivElement, CopyInteractionProps>(
  function CopyInteraction(
    {
      text,
      isPressed = false,
      copiedText,
      className,
      ...otherProps
    }: CopyInteractionProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/51387517 flex w-full flex-col items-start gap-2",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <div className="flex w-full flex-col items-start gap-2">
          <Button
            className={SubframeUtils.twClassNames("h-8 w-full flex-none", {
              hidden: isPressed,
            })}
            disabled={false}
            variant="brand-secondary"
            size="medium"
            icon={<FeatherClipboard />}
            iconRight={null}
            loading={false}
          >
            {text}
          </Button>
          <Button
            className={SubframeUtils.twClassNames(
              "hidden h-8 w-full flex-none",
              { flex: isPressed }
            )}
            disabled={false}
            variant="brand-secondary"
            size="medium"
            iconRight={<FeatherCheck />}
            loading={false}
          >
            {copiedText}
          </Button>
        </div>
      </div>
    );
  }
);

interface Dc_MetaIntro_ActionStackRootProps
  extends React.HTMLAttributes<HTMLDivElement> {
  actionSlot?: React.ReactNode;
  shareSlot?: React.ReactNode;
  className?: string;
}

const Dc_MetaIntro_ActionStackRoot = React.forwardRef<
  HTMLDivElement,
  Dc_MetaIntro_ActionStackRootProps
>(function Dc_MetaIntro_ActionStackRoot(
  {
    actionSlot,
    shareSlot,
    className,
    ...otherProps
  }: Dc_MetaIntro_ActionStackRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex w-full flex-wrap items-start gap-4",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {actionSlot ? (
        <div className="flex grow shrink-0 basis-0 flex-wrap items-start gap-4">
          {actionSlot}
        </div>
      ) : null}
      {shareSlot ? <div className="flex items-start">{shareSlot}</div> : null}
    </div>
  );
});

export const Dc_MetaIntro_ActionStack = Object.assign(
  Dc_MetaIntro_ActionStackRoot,
  {
    PdfDownloadButton,
    ListenButton,
    DoiButton,
    DoiPopover,
    CiteButton,
    CitePopover,
    CopyInteraction,
  }
);
